/* eslint-disable react/forbid-dom-props, react/forbid-component-props */
import classnames from 'classnames';
import { get, isEmpty, isUndefined, some, isString } from 'lodash';
import React, { useCallback, useEffect, useMemo, useRef, useState, } from 'react';
import { CellMeasurerCache, List } from 'react-virtualized';
import orderBy from '../../../../utils/orderBy';
import Statuses from '../../../../../model/Statuses';
import Scroll from '../../../other/Scroll';
import useLayoutDirection from '../../../utils/Translations/useLayoutDirection';
import useT from '../../../utils/Translations/useT';
import SearchField from '../SearchField';
import { RETURN_TYPE } from './SelectBox';
import styles from './SelectBox.scss';
import SelectBoxMenuItem from './SelectBoxMenuItem';
import nodeSortingOptionsSequence from '../../../../utils/nodeSortingOptionsSequence';
const SCROLL_DIRECTION_BACKWARD = -1;
const SCROLL_DIRECTION_FORWARD = 1;
const SCROLL_POSITION_CHANGE_REASON_OBSERVED = 'observed';
const TWO = 2;
const MENU_ITEM_HEIGHT = 36;
const MENU_VERTICAL_PADDING = 16;
const MAX_MENU_VERTICAL_PADDING = 10;
const DEFAULT_CACHE_PROPS = {
    fixedWidth: true,
    defaultHeight: 50,
};
const SelectBoxMenu = ({ props, isValueOk, findOptionByValue, controlRef, setSearch, containerRef, isOpen, search, addGroupName, comparator, closeMenu, }) => {
    const { autoWidth = null, setScrollHandler, liveSearch = false, resolveOptionVisibility = null, onCreate, maxHeight = 0, menuContainerStyle, isFilterBar = false, returnType = RETURN_TYPE.STRING, onChange = () => null, orderOptionsBy = nodeSortingOptionsSequence.iteratees, orderOptionsByOrders = nodeSortingOptionsSequence.orders, placeholder = '', value: selectedValue = '', emptyValue: baseEmptyValue = '', isPlaceholderClickable = true, required = false, noResultMessage = 'There is no data matching your criteria', itemValuePropName = 'value', itemTooltipPropName = '', groupTitleKeys = null, itemTitlePropName = 'name', options = [], onOptionTitle = null, onChangeQuery = null, loading = false, width, dataCy, menuClassName, setBodyClickHandler, calculateDropup, resetBodyClickHandler, calculateFixedBound, onOptionLabel, isItemCentered, onOptionContentRender, calculateDropupDebounced, renderFooter, hasBottomMargin = true, isInTable = true, searchInputClassName, searchInputWidthClassName, } = props;
    const t = useT();
    const { direction } = useLayoutDirection();
    const listStyle = useMemo(() => ({ width: '100%', direction }), [direction]);
    const [itemList, setItemList] = useState(null);
    const listRef = useRef();
    const setListRef = useCallback(r => (listRef.current = r), []);
    const cache = new CellMeasurerCache(DEFAULT_CACHE_PROPS);
    const handleInputChange = useCallback(search => {
        setSearch(search);
        onChangeQuery && onChangeQuery(search);
    }, [setSearch, onChangeQuery]);
    const handleSearchReset = useCallback(() => handleInputChange(''), [
        handleInputChange,
    ]);
    const handleCreate = useCallback(e => {
        e.preventDefault();
        e.stopPropagation();
        onCreate && onCreate(search);
    }, [onCreate, search]);
    const getScrollDirection = useCallback(scrollTop => {
        const { scrollTop: oldScrollTop, scrollDirectionVertical } = get(listRef.current, 'Grid.state', {});
        if (scrollTop !== oldScrollTop) {
            return scrollTop > oldScrollTop
                ? SCROLL_DIRECTION_FORWARD
                : SCROLL_DIRECTION_BACKWARD;
        }
        return scrollDirectionVertical;
    }, [listRef]);
    const handleScroll = useCallback(({ target }) => {
        const { scrollTop, scrollLeft } = target;
        const grid = get(listRef.current, 'Grid', {});
        grid._debounceScrollEnded();
        const scrollDirectionVertical = getScrollDirection(scrollTop);
        const totalColumnsWidth = grid.getTotalColumnsWidth();
        const totalRowsHeight = grid.getTotalRowsHeight();
        grid.setState({
            isScrolling: true,
            scrollDirectionVertical,
            scrollTop,
            scrollPositionChangeReason: SCROLL_POSITION_CHANGE_REASON_OBSERVED,
        });
        grid._invokeOnScrollMemoizer({
            scrollLeft,
            scrollTop,
            totalColumnsWidth,
            totalRowsHeight,
        });
    }, [listRef, getScrollDirection]);
    const _resolveOptionVisibility = useCallback(option => {
        if (resolveOptionVisibility) {
            return resolveOptionVisibility(option);
        }
        return (isUndefined(option.status) || option.status === Statuses.Active.value);
    }, [resolveOptionVisibility]);
    const getItemTitle = useCallback((item, groupIndex) => onOptionTitle
        ? onOptionTitle(addGroupName(item, groupIndex))
        : item[itemTitlePropName], [addGroupName, itemTitlePropName, onOptionTitle]);
    const getItemValue = useCallback(item => returnType === RETURN_TYPE.OPTION ? item : item[itemValuePropName], [itemValuePropName, returnType]);
    const getItemCode = useCallback(item => (returnType === RETURN_TYPE.OPTION ? item : item.code), [itemValuePropName, returnType]);
    const getOptionItem = useCallback((item, optionItemIndex = null) => {
        if (item.divider) {
            return item;
        }
        const tooltipTitle = itemTooltipPropName ? item[itemTooltipPropName] : '';
        const label = getItemTitle(item, optionItemIndex);
        const value = getItemValue(item);
        const code = getItemCode(item);
        const title = onOptionTitle
            ? onOptionTitle(addGroupName(item, optionItemIndex))
            : tooltipTitle;
        const active = comparator(value, selectedValue);
        return {
            title,
            label,
            value,
            active,
            code,
            disabled: item.disabled,
            searchable: true,
            status: item.status,
        };
    }, [
        addGroupName,
        comparator,
        getItemTitle,
        getItemValue,
        getItemCode,
        onOptionTitle,
        selectedValue,
        itemTooltipPropName,
    ]);
    const getOptionsListWithDivider = useCallback((optionItem, optionItemIndex) => {
        optionItem = optionItem.map(item => getOptionItem(item, optionItemIndex));
        if (!optionItem.length) {
            return optionItem;
        }
        if (groupTitleKeys) {
            optionItem.unshift({
                label: groupTitleKeys[optionItemIndex],
                header: true,
                disabled: true,
            });
        }
        else {
            optionItem.push({
                divider: true,
            });
        }
        return optionItem;
    }, [getOptionItem, groupTitleKeys]);
    const optionListCooked = useMemo(() => {
        const emptyValue = returnType === RETURN_TYPE.STRING
            ? String(baseEmptyValue)
            : baseEmptyValue;
        let optionList = options;
        if (orderOptionsBy) {
            const orderByFieldNames = Array.isArray(orderOptionsBy)
                ? orderOptionsBy
                : [orderOptionsBy];
            const orderByClauses = orderByFieldNames.map(fieldName => model => {
                const field = model[fieldName];
                if (isString(field)) {
                    return String(model[fieldName]).toLowerCase().replace(' ', '');
                }
                return model[fieldName];
            });
            optionList = [
                ...orderBy(options.filter(option => !Array.isArray(option)), orderByClauses, orderOptionsByOrders),
                ...options
                    .filter(Array.isArray)
                    .map(optionsArray => orderBy(optionsArray, orderByClauses, orderOptionsByOrders)),
            ];
        }
        optionList = optionList.reduce((acc, option, i) => {
            if (Array.isArray(option)) {
                const filteredOption = option.filter(_resolveOptionVisibility);
                if (!isEmpty(filteredOption)) {
                    acc.push(...getOptionsListWithDivider(option, i));
                }
            }
            else {
                if (_resolveOptionVisibility(option)) {
                    acc.push(getOptionItem(option));
                }
            }
            return acc;
        }, []);
        if (search) {
            const searchLower = search.toLowerCase();
            optionList = optionList.filter(({ label, searchable }) => !searchable || (label && label.toLowerCase().includes(searchLower)));
        }
        // remove split dividers and last one
        optionList = optionList.filter(({ divider }, index) => !divider ||
            (divider && optionList[index + 1] && !optionList[index + 1].divider));
        if ((search && !some(optionList, { searchable: true })) ||
            (isFilterBar && !optionList.length)) {
            // add no result line
            optionList.push({
                value: emptyValue,
                label: `${t(noResultMessage)}.`,
                aClass: 'select-title text-center',
                noResult: true,
                disabled: false,
            });
        }
        else if (placeholder) {
            // add placeholder line
            let renderedPlaceholder = placeholder;
            if (required) {
                renderedPlaceholder = `${placeholder} *`;
            }
            if (loading) {
                renderedPlaceholder = `... ${t('loading')}`;
            }
            optionList.unshift({
                label: renderedPlaceholder,
                disabled: !isPlaceholderClickable,
                value: emptyValue,
                aClass: 'select-title',
            });
        }
        return optionList;
    }, [
        orderOptionsByOrders,
        returnType,
        _resolveOptionVisibility,
        baseEmptyValue,
        getOptionItem,
        getOptionsListWithDivider,
        isFilterBar,
        isPlaceholderClickable,
        loading,
        noResultMessage,
        options,
        orderOptionsBy,
        placeholder,
        required,
        search,
        t,
    ]);
    const handleOnChange = useCallback(value => () => {
        const { option } = findOptionByValue(value);
        if (returnType === RETURN_TYPE.OPTION) {
            onChange && onChange(option, props);
        }
        else {
            value = option ? getItemValue(option) : baseEmptyValue;
            const result = isValueOk(value) && returnType === RETURN_TYPE.NUMBER
                ? Number(value)
                : value;
            onChange && onChange(result, props);
        }
        closeMenu();
    }, [
        closeMenu,
        baseEmptyValue,
        findOptionByValue,
        getItemValue,
        isValueOk,
        onChange,
        props,
        returnType,
    ]);
    const renderSingleItem = useCallback(({ key, index, parent, style }) => {
        const item = optionListCooked[index];
        if (!item) {
            return null;
        }
        return (<SelectBoxMenuItem key={key} cache={cache} dataCy={dataCy ? `${dataCy}-item` : undefined} handleOnChange={handleOnChange(item.value)} index={index} isItemCentered={isItemCentered} item={item} parent={parent} returnType={returnType} style={style} onOptionContentRender={onOptionContentRender} onOptionLabel={onOptionLabel}/>);
    }, [
        returnType,
        onOptionLabel,
        isItemCentered,
        onOptionContentRender,
        optionListCooked,
        handleOnChange,
        cache,
        dataCy,
    ]);
    useEffect(() => {
        if (isOpen && itemList) {
            setBodyClickHandler(closeMenu, containerRef.current);
            // scroll into view
            const selected = (itemList === null || itemList === void 0 ? void 0 : itemList.querySelector) && itemList.querySelector('li.active');
            if (selected instanceof HTMLElement) {
                const y = selected.offsetTop - itemList.offsetHeight / TWO;
                if (itemList.scrollTo) {
                    itemList.scrollTo(0, y);
                }
                else {
                    itemList.scrollTop = y;
                }
            }
            calculateDropup(controlRef === null || controlRef === void 0 ? void 0 : controlRef.current);
            calculateFixedBound({
                setHeight: true,
                hasSearch: liveSearch,
                controlRef: controlRef === null || controlRef === void 0 ? void 0 : controlRef.current,
            });
        }
        else {
            resetBodyClickHandler();
        }
    }, [
        setBodyClickHandler,
        calculateDropup,
        resetBodyClickHandler,
        calculateFixedBound,
        closeMenu,
        containerRef,
        controlRef,
        isOpen,
        itemList,
        liveSearch,
    ]);
    const handleSizeChange = useCallback(e => {
        if ((containerRef === null || containerRef === void 0 ? void 0 : containerRef.current) &&
            (e.target === window || e.target.contains(containerRef.current))) {
            if (isOpen) {
                calculateFixedBound({
                    setHeight: false,
                    hasSearch: liveSearch,
                    controlRef: controlRef === null || controlRef === void 0 ? void 0 : controlRef.current,
                });
            }
            else {
                calculateDropupDebounced(controlRef === null || controlRef === void 0 ? void 0 : controlRef.current);
            }
        }
    }, [
        calculateFixedBound,
        calculateDropupDebounced,
        containerRef,
        controlRef,
        isOpen,
        liveSearch,
    ]);
    useEffect(() => {
        calculateDropup(controlRef === null || controlRef === void 0 ? void 0 : controlRef.current);
        setScrollHandler(handleSizeChange);
    }, [calculateDropup, props, controlRef, handleSizeChange, setScrollHandler]);
    const minHeight = optionListCooked.length * MENU_ITEM_HEIGHT + MENU_VERTICAL_PADDING;
    return (<div className={classnames(menuClassName, 'dropdown-menu open', styles.positionFix, {
        [styles.fixed]: !isFilterBar,
        [styles.filterBarDropdown]: isFilterBar,
        [styles.autoWidth]: autoWidth,
    })} data-cy={dataCy ? `${dataCy}-menu` : undefined} style={{
        // maxHeight,
        minHeight: minHeight > maxHeight
            ? maxHeight + MAX_MENU_VERTICAL_PADDING
            : minHeight,
        ...menuContainerStyle,
    }}>
      {liveSearch && (<SearchField hasBottomMargin={hasBottomMargin} inputClassName={searchInputClassName} inputProps={{ autoFocus: true }} isDebouncingEnabled={false} isInTable={isInTable} value={search} widthClassName={classnames('searchbox', searchInputWidthClassName)} onChange={handleInputChange} onReset={handleSearchReset}/>)}
      {onCreate && <span onClick={handleCreate}/>}
      <Scroll autoHeightMax={maxHeight} className={classnames('dropdown-menu inner')} classNameInner={classnames('dropdown-menu inner', styles.scrollable)} getRef={setItemList} isOpen={isOpen} tagName="ul" onScroll={handleScroll}>
        <List ref={setListRef} autoHeight deferredMeasurementCache={cache} height={maxHeight} rowCount={optionListCooked.length} rowHeight={cache.rowHeight} rowRenderer={renderSingleItem} style={listStyle} width={width}/>
      </Scroll>
      {renderFooter && renderFooter()}
    </div>);
};
export default SelectBoxMenu;
